import React, { useEffect, useRef, useState } from 'react';
import { blockService } from '../services';
import { useDialog } from '../context';
import './portalkit/kit.css';

const REASONS = [
  { value: 'harassment', label: 'Harassment or bullying' },
  { value: 'spam', label: 'Spam or scam' },
  { value: 'impersonation', label: 'Pretending to be someone' },
  { value: 'inappropriate', label: 'Inappropriate content' },
  { value: 'safety', label: 'Safety concern' },
  { value: 'other', label: 'Something else' },
];

/**
 * Block / report actions for one person, shared by the profile three-dot menu
 * and the long-press menu in a thread.
 *
 * Blocking always confirms first, and the confirmation says where to undo it —
 * an action with no visible way back reads as permanent, and people avoid it
 * even when it's what they need.
 *
 * Reporting is offered separately from blocking because they are different
 * asks: "I don't want to see this person" versus "someone should look at this".
 * The report sheet offers blocking as a checkbox rather than assuming either.
 */
export default function UserActionsMenu({
  userId,
  displayName,
  open,
  onClose,
  onBlocked,
  messageId = null,
  eventId = null,
  anchor = 'right',
  /** 'slab' — the bordered white box (chat threads); 'portal' — the dark rounded panel the Portal Profile uses. */
  tone = 'slab',
}) {
  const { showConfirm, showAlert } = useDialog();
  const [reporting, setReporting] = useState(false);
  const [reason, setReason] = useState('harassment');
  const [details, setDetails] = useState('');
  const [alsoBlock, setAlsoBlock] = useState(true);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) { setReporting(false); setDetails(''); return undefined; }
    const onDocDown = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose?.(); };
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('mousedown', onDocDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const who = displayName || 'this person';

  const doBlock = async () => {
    onClose?.();
    const ok = await showConfirm({
      title: `BLOCK ${who.toUpperCase()}?`,
      message: `They won't be able to find you in search, message you, or see you on guest lists — and you won't see them either. You can undo this any time in Settings under Blocked People.`,
      confirmLabel: 'Block',
      cancelLabel: 'Cancel',
      variant: 'danger',
    });
    if (!ok) return;

    const r = await blockService.block(userId);
    if (r.success) {
      onBlocked?.(userId);
      showAlert({ title: 'BLOCKED', message: `${who} can no longer reach you. Undo it in Settings.`, type: 'info' });
    } else {
      showAlert({ title: 'ERROR', message: r.error || 'Could not block.', type: 'error' });
    }
  };

  const submitReport = async () => {
    setBusy(true);
    const r = await blockService.report({ userId, reason, details, messageId, eventId, alsoBlock });
    setBusy(false);
    onClose?.();
    if (r.success) {
      if (alsoBlock) onBlocked?.(userId);
      showAlert({
        title: 'REPORT SENT',
        message: 'Thanks — someone will review this. We may follow up by email.',
        type: 'info',
      });
    } else {
      showAlert({ title: 'ERROR', message: r.error || 'Could not send the report.', type: 'error' });
    }
  };

  if (tone === 'portal') {
    const item = { display: 'block', width: '100%', padding: '12px 16px', border: 'none', borderRadius: 14, background: 'transparent', textAlign: 'left', fontFamily: 'inherit', fontSize: 11, fontWeight: 700, letterSpacing: '.14em', textTransform: 'uppercase', cursor: 'pointer', transition: 'background-color .2s' };
    const field = { width: '100%', boxSizing: 'border-box', padding: '10px 14px', border: 'none', borderRadius: 14, background: '#000', color: '#fff', fontFamily: 'inherit', fontSize: 12, boxShadow: 'inset 0 0 0 1px #2A2A2A', outline: 'none' };
    return (
      <div
        ref={ref}
        role="menu"
        className="pk"
        style={{ position: 'absolute', top: 'calc(100% + 8px)', [anchor === 'left' ? 'left' : 'right']: 0, zIndex: 40, width: 272, maxWidth: 'calc(100vw - 32px)', padding: 6, borderRadius: 20, background: '#161616', color: '#fff', boxShadow: 'inset 0 0 0 1px #2A2A2A, 0 12px 32px rgba(0,0,0,.5)', boxSizing: 'border-box' }}
      >
        {!reporting ? (
          <>
            <button type="button" role="menuitem" onClick={doBlock} className="pk-row-hover" style={{ ...item, color: '#fff' }}>Block {who}</button>
            <button type="button" role="menuitem" onClick={() => setReporting(true)} className="pk-row-hover" style={{ ...item, color: '#FF66C4' }}>Report</button>
          </>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 10 }}>
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.14em', textTransform: 'uppercase' }}>Report {who}</span>
            <select value={reason} onChange={(e) => setReason(e.target.value)} style={field}>
              {REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
            <textarea value={details} onChange={(e) => setDetails(e.target.value)} rows={3} maxLength={2000} placeholder="Anything that helps us understand (optional)" className="pk-in-dark" style={{ ...field, resize: 'vertical' }} />
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 11, color: '#BDBDBD' }}>
              <input type="checkbox" checked={alsoBlock} onChange={(e) => setAlsoBlock(e.target.checked)} style={{ marginTop: 2 }} />
              Also block them
            </label>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" onClick={submitReport} disabled={busy} className="pk pk-btn pk-primary-on-black" style={{ flex: 1, height: 38, fontSize: 10, fontWeight: 800, letterSpacing: '.16em', textTransform: 'uppercase' }}>{busy ? 'Sending…' : 'Send report'}</button>
              <button type="button" onClick={() => setReporting(false)} className="pk pk-btn pk-outline-on-black" style={{ height: 38, padding: '0 14px', fontSize: 10, fontWeight: 700, letterSpacing: '.14em', textTransform: 'uppercase' }}>Back</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      ref={ref}
      role="menu"
      className={`absolute z-40 mt-1 w-64 border-2 border-black bg-white shadow-lg ${anchor === 'left' ? 'left-0' : 'right-0'}`}
    >
      {!reporting ? (
        <div className="py-1">
          <button
            type="button"
            role="menuitem"
            onClick={doBlock}
            className="block w-full px-4 py-3 text-left font-mono text-xs font-bold uppercase tracking-widest text-black hover:bg-black hover:text-white"
          >
            Block {who}
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => setReporting(true)}
            className="block w-full px-4 py-3 text-left font-mono text-xs font-bold uppercase tracking-widest text-red-600 hover:bg-red-600 hover:text-white"
          >
            Report
          </button>
        </div>
      ) : (
        <div className="p-4">
          <p className="font-mono text-xs font-bold uppercase tracking-widest">Report {who}</p>
          <select
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="mt-3 w-full border-2 border-black px-2 py-2 font-mono text-xs"
          >
            {REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          <textarea
            value={details}
            onChange={(e) => setDetails(e.target.value)}
            rows={3}
            maxLength={2000}
            placeholder="Anything that helps us understand (optional)"
            className="mt-2 w-full border-2 border-black px-2 py-2 font-mono text-xs"
          />
          <label className="mt-2 flex items-start gap-2 font-mono text-[11px] text-gray-700">
            <input type="checkbox" checked={alsoBlock} onChange={(e) => setAlsoBlock(e.target.checked)} className="mt-0.5" />
            Also block them
          </label>
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={submitReport}
              disabled={busy}
              className="flex-1 border-2 border-black bg-black py-2 font-mono text-[10px] font-bold uppercase tracking-widest text-white disabled:opacity-40"
            >
              {busy ? 'Sending…' : 'Send report'}
            </button>
            <button
              type="button"
              onClick={() => setReporting(false)}
              className="border-2 border-black px-3 py-2 font-mono text-[10px] font-bold uppercase tracking-widest"
            >
              Back
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
